import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CoinPackagesTab } from "@/components/manager/CoinPackagesTab";
import { DailyRewardManagement } from "@/components/manager/DailyRewardManagement";
import { ShopItemsTab } from "@/components/manager/ShopItemsTab";
import { SoundManagementTab } from "@/components/manager/SoundManagementTab";
import { DefaultSoundsTab } from "@/components/manager/DefaultSoundsTab";
import { CardDecksTab } from "@/components/manager/CardDecksTab";
import { CardBacksTab } from "@/components/manager/CardBacksTab";
import { AchievementsTab } from "@/components/manager/AchievementsTab";
import { GameSettingsTab } from "@/components/admin/GameSettingsTab";
import { CustomizationTab } from "@/components/manager/CustomizationTab";
import { AnnouncementsTab } from "@/components/manager/AnnouncementsTab";
import { apiRequest } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Layers,
  ShoppingCart,
  Package,
  Music,
  Badge,
  Coins,
  Check,
  RefreshCw,
  ImageIcon,
  Users,
  Award,
  GamepadIcon,
  DollarSign,
  Volume2,
  Settings,
  UserCircle,
  Bell,
  ChevronLeft,
  Menu,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EnhancedHeader } from "@/components/ui/enhanced-header";

interface Statistics {
  totalDecks: number;
  activeDecks: number;
  premiumDecks: number;
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  totalGames: number;
  activeGames: number;
  completedGames: number;
  totalRevenue: number;
  revenueThisMonth: number;
  totalSoundPacks: number;
  totalAchievements: number;
  totalShopItems: number;
}

// LoadingCard component moved outside
const LoadingCard = ({ title }: { title: string }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-8 bg-muted/20 animate-pulse rounded" />
    </CardContent>
  </Card>
);

export default function ManagerPage() {
  const [activeTab, setActiveTab] = useState("stats");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Add useEffect to handle mobile sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Define navigation items with their labels and icons
  const navItems = [
    { id: "stats", label: "Dashboard", icon: <BarChart className="h-5 w-5" /> },
    { id: "announcements", label: "Announcements", icon: <Bell className="h-5 w-5" /> },
    { id: "decks", label: "Card Decks", icon: <Layers className="h-5 w-5" /> },
    { id: "cardBacks", label: "Card Backs", icon: <ImageIcon className="h-5 w-5" /> },
    { id: "customization", label: "Customization", icon: <UserCircle className="h-5 w-5" /> },
    { id: "shop", label: "Shop Items", icon: <ShoppingCart className="h-5 w-5" /> },
    { id: "sounds", label: "Sounds", icon: <Music className="h-5 w-5" /> },
    { id: "defaultSounds", label: "Default Sounds", icon: <Volume2 className="h-5 w-5" /> },
    { id: "rewards", label: "Daily Rewards", icon: <Package className="h-5 w-5" /> },
    { id: "achievements", label: "Achievements", icon: <Badge className="h-5 w-5" /> },
    { id: "coinPackages", label: "Coin Packages", icon: <Coins className="h-5 w-5" /> },
    { id: "gameSettings", label: "Game Settings", icon: <Settings className="h-5 w-5" /> },
  ];

  // Render the appropriate content based on activeTab
  const renderContent = () => {
    switch (activeTab) {
      case "decks":
        return <CardDecksTab />;
      case "cardBacks":
        return <CardBacksTab />;
      case "customization":
        return <CustomizationTab />;
      case "shop":
        return <ShopItemsTab />;
      case "sounds":
        return <SoundManagementTab />;
      case "defaultSounds":
        return <DefaultSoundsTab />;
      case "rewards":
        return <DailyRewardManagement />;
      case "achievements":
        return <AchievementsTab />;
      case "announcements":
        return <AnnouncementsTab />;
      case "stats":
        return <DeckStatistics />;
      case "coinPackages":
        return <CoinPackagesTab />;
      case "gameSettings":
        return <GameSettingsTab />;
      default:
        return <DeckStatistics />;
    }
  };

  return (
    <div>
      <EnhancedHeader
        title="Manager Dashboard"
        backPath="/games"
        showBackButton={true}
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        }
      />

      <div className="flex min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black">
        {/* Desktop Sidebar Navigation */}
        <div className={cn(
          "relative transition-all duration-300 ease-in-out hidden md:flex",
          isCollapsed ? "w-16" : "w-64",
          "bg-black/60 backdrop-blur-sm border-r border-green-500/30 p-4 flex-col"
        )}>
          <div className="flex items-center justify-between mb-6">
            {!isCollapsed && (
              <div>
                <h1 className="text-xl font-bold text-white">Manager Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage card decks, shop items, daily rewards, and view statistics
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <ChevronLeft className={cn(
                "h-4 w-4 transition-transform",
                isCollapsed && "rotate-180"
              )} />
            </Button>
          </div>
          
          <nav className="space-y-1 flex-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  activeTab === item.id
                    ? "bg-green-600/20 text-green-400 font-medium"
                    : "text-gray-300 hover:bg-green-600/10 hover:text-green-300"
                )}
              >
                {item.icon}
                {!isCollapsed && item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Mobile Sidebar Navigation */}
        <div className={cn(
          "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden",
          isMobileMenuOpen ? "opacity-100 pointer-events-auto mt-10" : "opacity-0 pointer-events-none"
        )}>
          <div className={cn(
            "absolute inset-y-0 left-0 w-64 bg-black/90 border-r border-green-500/30 p-4 transition-transform duration-300",
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          )}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-xl font-bold text-white">Manager Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage card decks and settings
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <nav className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-md text-base transition-colors",
                    activeTab === item.id
                      ? "bg-green-600/20 text-green-400 font-medium"
                      : "text-gray-300 hover:bg-green-600/10 hover:text-green-300"
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Accessible backdrop button */}
          <button
            className="absolute inset-0 -z-10 w-full h-full cursor-default"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
              }
            }}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-4 sm:p-6 overflow-auto pb-20">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{navItems.find(item => item.id === activeTab)?.label}</h2>
            </div>
            
            {/* Content based on selected tab */}
            <div className="space-y-4">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeckStatistics() {
  const {
    data: stats,
    isLoading,
    error,
    isFetching
  } = useQuery({
    queryKey: ["/api/manager/stats"],
    queryFn: () => apiRequest<Statistics>("/api/manager/stats"),
    staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });

  // Get the current date for display
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-6 w-32 bg-muted/20 animate-pulse rounded mb-2" />
            <div className="h-4 w-48 bg-muted/20 animate-pulse rounded" />
          </div>
        </div>

        <div className="space-y-6">
          {/* User Statistics Loading State */}
          <div>
            <div className="h-6 w-40 bg-muted/20 animate-pulse rounded mb-3" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-3">
              <LoadingCard title="Total Users" />
              <LoadingCard title="Active Users" />
              <LoadingCard title="New Users" />
            </div>
          </div>

          {/* Game Statistics Loading State */}
          <div>
            <div className="h-6 w-40 bg-muted/20 animate-pulse rounded mb-3" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <LoadingCard title="Total Games" />
              <LoadingCard title="Active Games" />
              <LoadingCard title="Completed Games" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading statistics</h3>
        <p className="text-muted-foreground">{error.message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["/api/manager/stats"] })
          }
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dashboard header with date */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-300">Platform Overview</h3>
          <p className="text-sm text-muted-foreground">{formattedDate}</p>
        </div>
        <Button 
          variant="outline" 
          className="flex items-center gap-2 text-sm" 
          onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/manager/stats"] })}
          disabled={isFetching}
        >
          <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
          {isFetching ? "Refreshing..." : "Refresh Data"}
        </Button>
      </div>

      {/* User Statistics Section */}
      <div>
        <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-400" />
          User Statistics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalUsers ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Registered accounts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.activeUsers ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">New Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.newUsersThisMonth ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">This month</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Game Statistics Section */}
      <div>
        <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
          <GamepadIcon className="h-5 w-5 text-green-400" />
          Game Statistics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalGames ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Active Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.activeGames ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Currently in progress</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Completed Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.completedGames ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">All time</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Revenue Statistics Section */}
      <div>
        <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-yellow-400" />
          Revenue Statistics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats?.totalRevenue?.toFixed(2) ?? "0.00"}</div>
              <p className="text-xs text-muted-foreground mt-1">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats?.revenueThisMonth?.toFixed(2) ?? "0.00"}</div>
              <p className="text-xs text-muted-foreground mt-1">Current month</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Statistics */}
      <div>
        <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
          <Package className="h-5 w-5 text-purple-400" />
          Content Statistics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Decks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Layers className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">{stats?.totalDecks ?? 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Active Decks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <div className="text-2xl font-bold">{stats?.activeDecks ?? 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Premium Decks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Coins className="h-5 w-5 text-yellow-500 mr-2" />
                <div className="text-2xl font-bold">{stats?.premiumDecks ?? 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Sound Packs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Music className="h-5 w-5 text-blue-500 mr-2" />
                <div className="text-2xl font-bold">{stats?.totalSoundPacks ?? 0}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* More Content Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Shop Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <ShoppingCart className="h-5 w-5 text-emerald-500 mr-2" />
              <div className="text-2xl font-bold">{stats?.totalShopItems ?? 0}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Available in the shop</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Achievements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Award className="h-5 w-5 text-amber-500 mr-2" />
              <div className="text-2xl font-bold">{stats?.totalAchievements ?? 0}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Available for users</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
